import React, { useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import PropTypes from 'prop-types';
import I18n from 'modules/i18n';
import AutoSave from 'common/Forms/AutoSave';
import Select from 'react-select';
import styles from './BackgroundPositionManager.module.scss';
import './react-select-reset.css';

const BackgroundPositionManager = ({
  customMatcher,
  defaultValue,
  disableHorizontal,
  disableVertical,
  horizontalLabel,
  idPrefix,
  includeVertical,
  initialSliderPosition,
  initialSliderPositionY,
  livePreview,
  minWidth,
  object,
  objectRef,
  options,
  parseCustom,
  relocate,
  submitHandler,
  verticalLabel,
}) => {
  const isCustom = useMemo(() => {
    const { backgroundPosition } = object;

    return backgroundPosition && backgroundPosition.match(customMatcher);
  }, [object.backgroundPosition, customMatcher]);

  // Background stores custom as `center N%` (vertical only); consumers can override how it's read/written.
  const resolvedParseCustom = parseCustom || (value => ({
    customPosition: value.replace('center ', ''),
    customPositionY: initialSliderPositionY,
  }));

  const resolvedLivePreview = livePreview || ((values) => {
    $(objectRef.current).css({ backgroundPosition: `center ${values.customPosition}%` });
  });

  const initialBgPosition = ({ backgroundPosition }) => {
    if (backgroundPosition) {
      if (isCustom) return 'custom';

      return backgroundPosition;
    }

    return defaultValue;
  };

  const parsedCustom = isCustom ? resolvedParseCustom(object.backgroundPosition) : null;

  const initialValues = {
    backgroundPosition: initialBgPosition(object),
    customPosition: parsedCustom ? parsedCustom.customPosition : initialSliderPosition,
    customPositionY: parsedCustom ? parsedCustom.customPositionY : initialSliderPositionY,
  };

  /* eslint-disable react/prop-types -- `input` is react-final-form's FieldRenderProps, not component props */
  const renderDropdown = ({ input }) => (
    <div>
      <label htmlFor={idPrefix}>{I18n.t('position_type')}</label>
      <Select
        {...input}
        clearable={false}
        inputId={idPrefix}
        options={options}
        searchable={false}
        value={options.find(option => option.value === input.value)}
        onChange={(opt) => {
          input.onChange(opt.value);
          if (relocate) relocate();
        }}
      />
    </div>
  );

  const renderSlider = ({ input }, values) => {
    const isVertical = input.name === 'customPositionY';
    const axisLabel = isVertical ? verticalLabel : horizontalLabel;
    const sliderId = `${idPrefix}-${input.name}`;
    const disabled = isVertical ? disableVertical : disableHorizontal;

    return (
      <div className={`${styles.rangeContainer} ${isVertical ? styles.vertical : ''}`}>
        <label className="hidden" htmlFor={sliderId}>{axisLabel}</label>
        <input
          className={`${styles.rangeInput} ${isVertical ? styles.verticalInput : ''}`}
          disabled={disabled}
          id={sliderId}
          max="100"
          min="0"
          type="range"
          {...input}
          onChange={(e) => {
            resolvedLivePreview({ ...values, [input.name]: e.target.value });
            input.onChange(e.target.value);
          }}
        />
        <input
          aria-label={axisLabel}
          className={styles.rangeValueInput}
          disabled={disabled}
          type="text"
          {...input}
          value={`${(input.value || '0')}%`}
          onChange={(e) => {
            input.onChange(e.target.value.replace('%', ''));
          }}
        />
      </div>
    );
  };
  /* eslint-enable react/prop-types */

  return (
    <Form
      keepDirtyOnReinitialize
      initialValues={initialValues}
      render={({ handleSubmit, values }) => (
        <form className={`${styles.form} ${minWidth ? styles.minWidth : ''}`} onSubmit={handleSubmit}>
          <AutoSave debounce={400} save={submitHandler} />
          <Field
            name="backgroundPosition"
            render={renderDropdown}
            value={options[0].value}
          />
          {values.backgroundPosition === 'custom' && (
            <Field
              name="customPosition"
              render={fieldProps => renderSlider(fieldProps, values)}
            />
          )}
          {values.backgroundPosition === 'custom' && includeVertical && (
            <Field
              name="customPositionY"
              render={fieldProps => renderSlider(fieldProps, values)}
            />
          )}
        </form>
      )}
      onSubmit={submitHandler}
    />
  );
};

BackgroundPositionManager.defaultProps = {
  customMatcher: /center \d+/g,
  defaultValue: 'center center',
  disableHorizontal: false,
  disableVertical: false,
  horizontalLabel: 'Custom Range',
  idPrefix: 'backgroundPosition',
  includeVertical: false,
  initialSliderPosition: '50',
  initialSliderPositionY: '100',
  livePreview: null,
  minWidth: true,
  objectRef: React.createRef(),
  options: [
    { label: 'Top', value: 'center top' },
    { label: 'Bottom', value: 'center bottom' },
    { label: 'Center', value: 'center center' },
    { label: 'Custom', value: 'custom' },
  ],
  parseCustom: null,
  verticalLabel: 'Custom Range',
};

BackgroundPositionManager.propTypes = {
  customMatcher: PropTypes.instanceOf(RegExp),
  defaultValue: PropTypes.string,
  disableHorizontal: PropTypes.bool,
  disableVertical: PropTypes.bool,
  horizontalLabel: PropTypes.string,
  idPrefix: PropTypes.string,
  includeVertical: PropTypes.bool,
  initialSliderPosition: PropTypes.string,
  initialSliderPositionY: PropTypes.string,
  livePreview: PropTypes.func,
  minWidth: PropTypes.bool,
  object: PropTypes.shape({
    backgroundPosition: PropTypes.string,
  }).isRequired,
  objectRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
  parseCustom: PropTypes.func,
  relocate: PropTypes.func,
  submitHandler: PropTypes.func.isRequired,
  verticalLabel: PropTypes.string,
};

export default BackgroundPositionManager;
